import { IoMdArrowBack } from "react-icons/io";
import { useNavigate, useLocation } from "react-router-dom";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchWatchHistory, clearWatchHistory } from "../../redux/ApiSlice/ActivitySlice";
import VideoCard from "../../components/common/VideoCard";
import useSelectVideo from "../../hooks/useSelectVideo";
import ConfirmModal from "../../components/Modals/ConfirmModal";
import SkeletonVideoCard from "../../components/skeleton/SkeletonVideoCard";

const WatchHistory = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const scrollRef = useRef(null);
    const selectVideo = useSelectVideo();
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const {
        watchedVideos,
        totalWatchedCount,
        next,
        watchHistoryLoading,
        watchHistoryLoadingMore,
        watchHistoryError,
        clearLoading
    } = useSelector((state) => state.YourActivity);

    useEffect(() => {
        dispatch(fetchWatchHistory());
    }, [dispatch]);

    const handleSelectVideo = async (id) => {
        await selectVideo(id, watchedVideos, location.pathname);
    };

    const handleClearHistory = () => {
        setShowConfirmModal(true);
    };

    const handleConfirmClear = async () => {
        try {
            await dispatch(clearWatchHistory()).unwrap();
        } catch {
            // clearWatchHistory error is handled in Redux state
        } finally {
            setShowConfirmModal(false);
        }
    };

    const handleScroll = useCallback(() => {
        const container = scrollRef.current;
        if (!container || !next || watchHistoryLoadingMore) {
            return;
        }

        const hasReachedBottom =
            container.scrollTop + container.clientHeight >= container.scrollHeight - 10;

        if (hasReachedBottom) {
            dispatch(fetchWatchHistory({ nextUrl: next, isLoadMore: true }));
        }
    }, [dispatch, watchHistoryLoadingMore, next]);

    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;

        container.addEventListener("scroll", handleScroll);

        return () => {
            container.removeEventListener("scroll", handleScroll);
        };
    }, [handleScroll]);

    return (
        <>
            <div
                ref={scrollRef}
                className="h-full bg-white dark:bg-[#1E1D21] overflow-y-scroll relative"
            >
                <div className="sticky top-0 z-50">
                    <div className="relative bg-[#BDE6FF] rounded-b-xl h-12 flex items-center justify-center">
                        <IoMdArrowBack
                            onClick={() => navigate("/settings/youractivity")}
                            className="absolute left-2 top-1/2 transform -translate-y-1/2 text-[22px] text-black dark:text-black cursor-pointer"
                        />
                        <p className="font-bold text-[16px] text-black dark:text-black">
                            Watch History
                        </p>
                    </div>
                </div>

                {/* Loading state */}
                {watchHistoryLoading && (
                    <SkeletonVideoCard />
                )}

                <div className="p-2">
                    {/* Header with clear button */}
                    {watchedVideos.length > 0 && (
                        <div className="flex justify-between items-center mb-4">
                            <p className="text-[14px] text-gray-600 dark:text-white">
                                Total watched: {totalWatchedCount}
                            </p>
                            <button
                                onClick={handleClearHistory}
                                disabled={clearLoading}
                                className="px-4 py-2 bg-[#BDE6FF] text-black rounded-lg disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                            >
                                {clearLoading ? "Clearing..." : "Clear History"}
                            </button>
                        </div>
                    )}

                    {/* Error state */}
                    {watchHistoryError && (
                        <div className="text-center py-10">
                            <p className="text-[14px] text-red-500">{watchHistoryError}</p>
                        </div>
                    )}

                    {/* Empty state */}
                    {!watchHistoryLoading && !watchHistoryError && watchedVideos.length === 0 && (
                        <div className="text-center py-10">
                            <p className="text-[14px] text-gray-500 dark:text-gray-400">No watch history yet</p>
                        </div>
                    )}

                    {/* Video list */}
                    {!watchHistoryLoading && watchedVideos.length > 0 && (
                        <div className="grid grid-cols-3 gap-1">
                            {watchedVideos.map((item) => (
                                <VideoCard
                                    key={item.id}
                                    id={item.id}
                                    hideSound={false}
                                    body={item.post_description}
                                    belowDecs={true}
                                    threeDots={false}
                                    video={item.video_url}
                                    likes_count={item.likes_count}
                                    view_count={item.view_count}
                                    authorId={item.created_by?.id}
                                    selectVideo={handleSelectVideo}
                                    avatar_url={item.created_by.avatar_url}
                                    showDelete={false}
                                    thumbnail={item.thumbnail_url}
                                />
                            ))}
                        </div>
                    )}

                    {/* Loading more indicator */}
                    {watchHistoryLoadingMore && (
                        <div className="flex justify-center items-center py-4">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
                        </div>
                    )}
                </div>
            </div>

            <ConfirmModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onConfirm={handleConfirmClear}
                title="Clear Watch History"
                message="Are you sure you want to clear your watch history?"
            />
        </>
    );
};

export default WatchHistory;